/**
 * Encrypted key backup — Threema Safe-style.
 *
 * Backup code: 20 Crockford Base32 chars (100 bits entropy)
 * KDF: Argon2id (INTERACTIVE: 2 ops, 64 MB)
 * Encryption: XChaCha20-Poly1305
 * Wire format: salt(16) || nonce(24) || ciphertext+tag
 */

import sodium from "libsodium-wrappers-sumo";

// Crockford Base32 alphabet — no I, L, O, U
const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

// Argon2id parameters (INTERACTIVE)
const ARGON2_OPSLIMIT = 2;
const ARGON2_MEMLIMIT = 67_108_864; // 64 MB

const SALT_BYTES = 16;
const NONCE_BYTES = 24; // XChaCha20-Poly1305
const KEY_BYTES = 32;
const BACKUP_VERSION = 1;
const MAX_BACKUP_SIZE = 512 * 1024; // 512 KB

// ── Types ──

export interface BackupBundle {
  version: 1;
  createdAt: string;
  deviceId: string;
  identityKeypair: { publicKey: string; privateKey: string };
  ephemeralKeypair: { publicKey: string; privateKey: string };
  fingerprint: string;
  ratchets: Record<string, string>;
  rooms?: Record<string, {
    roomId: string;
    name: string;
    conversationIds: string[];
    encryptionMode?: string;
    ownSenderKeyChain?: string;
    peerSenderKeyState?: string;
    distributedTo?: string[];
  }>;
  sessions?: Record<string, {
    ownerDeviceId: string;
    ratchetState: string;
    activated?: boolean;
  }>;
  a2aChannels?: Record<string, unknown>;
  topics?: Array<{ id: string; name: string; isDefault: boolean }>;
  defaultTopicId?: string;
  groupId?: string;
  hubAddress?: string;
}

// ── Backup Code Generation ──

/**
 * Generate a 20-character Crockford Base32 backup code (100 bits entropy).
 */
export async function generateBackupCode(): Promise<string> {
  await sodium.ready;
  // 13 random bytes = 104 bits; we use 100 bits (20 × 5-bit chars)
  const bytes = sodium.randombytes_buf(13);
  // Convert to a big number via bit manipulation
  let bits = 0n;
  for (const b of bytes) {
    bits = (bits << 8n) | BigInt(b);
  }
  // Shift right to get exactly 100 bits
  bits >>= 4n;

  let code = "";
  for (let i = 0; i < 20; i++) {
    const idx = Number(bits & 0x1fn);
    code = CROCKFORD[idx] + code;
    bits >>= 5n;
  }
  return code;
}

/**
 * Format a backup code with dashes: XXXX-XXXX-XXXX-XXXX-XXXX
 */
export function formatBackupCode(code: string): string {
  const clean = code.replace(/[-\s]/g, "").toUpperCase();
  return clean.match(/.{1,4}/g)?.join("-") ?? clean;
}

/**
 * Normalize user input: strip dashes/spaces, uppercase.
 */
export function normalizeBackupCode(input: string): string {
  return input.replace(/[-\s]/g, "").toUpperCase();
}

// ── Argon2id KDF ──

/**
 * Derive a 32-byte key from a backup code + salt using Argon2id.
 */
export async function deriveBackupKey(
  code: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.crypto_pwhash(
    KEY_BYTES,
    code,
    salt,
    ARGON2_OPSLIMIT,
    ARGON2_MEMLIMIT,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

// ── Encrypt / Decrypt ──

/**
 * Encrypt a BackupBundle with a backup code.
 * Output: salt(16) || nonce(24) || ciphertext+tag
 */
export async function encryptBackup(
  bundle: BackupBundle,
  code: string,
): Promise<Uint8Array> {
  await sodium.ready;
  const salt = sodium.randombytes_buf(SALT_BYTES);
  const key = await deriveBackupKey(code, salt);
  return encryptBackupWithKey(bundle, key, salt);
}

/**
 * Encrypt a BackupBundle with a pre-derived key + salt.
 * Used by auto-backup to avoid re-running Argon2id.
 * Output: salt(16) || nonce(24) || ciphertext+tag
 */
export async function encryptBackupWithKey(
  bundle: BackupBundle,
  key: Uint8Array,
  salt: Uint8Array,
): Promise<Uint8Array> {
  await sodium.ready;
  const plaintext = sodium.from_string(JSON.stringify(bundle));
  const nonce = sodium.randombytes_buf(NONCE_BYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintext,
    null,
    null,
    nonce,
    key,
  );

  // Wire format: salt || nonce || ciphertext
  const result = new Uint8Array(SALT_BYTES + NONCE_BYTES + ciphertext.length);
  result.set(salt, 0);
  result.set(nonce, SALT_BYTES);
  result.set(ciphertext, SALT_BYTES + NONCE_BYTES);
  return result;
}

/**
 * Decrypt an encrypted backup blob with a backup code.
 * Throws on wrong code or corrupt data.
 */
export async function decryptBackup(
  encrypted: Uint8Array,
  code: string,
): Promise<BackupBundle> {
  await sodium.ready;
  const minLen = SALT_BYTES + NONCE_BYTES + sodium.crypto_aead_xchacha20poly1305_ietf_ABYTES;
  if (encrypted.length < minLen) {
    throw new Error("Incorrect backup code or corrupt backup");
  }

  const salt = encrypted.slice(0, SALT_BYTES);
  const nonce = encrypted.slice(SALT_BYTES, SALT_BYTES + NONCE_BYTES);
  const ciphertext = encrypted.slice(SALT_BYTES + NONCE_BYTES);

  const key = await deriveBackupKey(code, salt);

  let plaintext: Uint8Array;
  try {
    plaintext = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      ciphertext,
      null,
      nonce,
      key,
    );
  } catch {
    throw new Error("Incorrect backup code or corrupt backup");
  }

  let bundle: BackupBundle;
  try {
    bundle = JSON.parse(sodium.to_string(plaintext));
  } catch {
    throw new Error("Incorrect backup code or corrupt backup");
  }

  if (bundle.version !== BACKUP_VERSION) {
    throw new Error(`Unsupported backup version: ${bundle.version}`);
  }

  return bundle;
}

// ── Code Hash ──

/**
 * BLAKE2b-256 hash of a backup code — used as "is configured?" indicator.
 * NOT the same as Argon2id key derivation (different algo, no salt).
 */
export async function hashBackupCode(code: string): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.crypto_generichash(32, sodium.from_string(code));
}
